import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import {
  ALL_CONTACT_SUBMISSION_STATUSES,
  type ContactOperationType,
  type ContactSubmissionStatus,
} from '@etiquetachef/types';
import { toast } from '@/lib/notifications';
import { formatDateTime } from '@/lib/format';
import { updateContactSubmissionStatusCallable } from '@/lib/callables';
import { useContactSubmissions } from './useContactSubmissions';
import type { ContactSubmissionRecord } from '@/lib/firestoreContact';

type StatusFilter = 'ALL' | ContactSubmissionStatus;

const STATUS_COLOR: Record<ContactSubmissionStatus, 'blue' | 'grey'> = {
  NEW: 'blue',
  READ: 'grey',
  ARCHIVED: 'grey',
};

/**
 * Tela `/contact/messages`: triagem dos leads do formulário de contato da landing.
 *
 * Origem: sem issue — commit aae23995a, 2026-06-18 20:10 +0200 (tela de contato).
 *
 * Por quê: objetivo da spec — triar os leads (ler, arquivar). A resposta ao
 * lead sai por e-mail (Reply-To = e-mail do cliente), não por esta tela.
 *
 * Contrato: lista via `useContactSubmissions`; seleção múltipla restrita aos
 * itens visíveis no filtro; "Marcar como lido"/"Arquivar" chamam
 * `updateContactSubmissionStatus` uma vez por item em paralelo
 * (`Promise.allSettled`) — falha parcial vira um toast de erro único, sem
 * dizer quais itens falharam (o listener mostra o estado real logo em seguida).
 *
 * Usado por: `routes/router.tsx` (rota `contact/messages`, sob `RequireSuperAdmin`);
 * item "Contato" do `AdminLayout`.
 *
 * Arquitetura: escrita só pela callable `updateContactSubmissionStatus`
 * (`apps/functions/src/contact/`) — as Rules não deixam o cliente gravar
 * `contactSubmissions`. Spec: `docs/screens/admin-contact.md`.
 *
 * Dicas: o filtro "Todos" esconde os ARCHIVED de propósito (só aparecem em
 * "Arquivados"); a seleção é recortada pelo filtro para a ação em massa nunca
 * atingir linha que o operador não está vendo.
 */
export function ContactMessagesPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const state = useContactSubmissions();
  const [filter, setFilter] = useState<StatusFilter>('ALL');
  const [selected, setSelected] = useState<ContactSubmissionRecord[]>([]);
  // Status sendo aplicado em massa (READ/ARCHIVED), ou `null` quando ocioso.
  // Só o botão correspondente mostra spinner; ambos ficam desabilitados.
  const [applying, setApplying] = useState<ContactSubmissionStatus | null>(null);

  useEffect(() => {
    if (state.kind === 'ERROR') toast.error(t('contact.messages.errorLoad'));
  }, [state.kind, t]);

  const items: ContactSubmissionRecord[] = useMemo(() => {
    if (state.kind !== 'READY') return [];
    // Arquivados só aparecem no filtro "Arquivados"; o "Todos" os esconde.
    if (filter === 'ALL') return state.items.filter((i) => i.status !== 'ARCHIVED');
    return state.items.filter((i) => i.status === filter);
  }, [state, filter]);

  // Mantém a seleção restrita aos itens visíveis no filtro atual.
  const selectedVisible = useMemo(
    () => selected.filter((s) => items.some((i) => i.id === s.id)),
    [selected, items],
  );

  /** Aplica `status` a todos os selecionados visíveis, um callable por item. */
  async function changeStatus(status: ContactSubmissionStatus): Promise<void> {
    const ids = selectedVisible.map((i) => i.id);
    if (ids.length === 0) return;
    setApplying(status);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => updateContactSubmissionStatusCallable({ id, status })),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        toast.error(t('contact.messages.toast.updateFailed'));
      } else {
        toast.success(t('contact.messages.toast.updated'));
      }
      setSelected([]);
    } finally {
      setApplying(null);
    }
  }

  /** Rótulo traduzido do tipo de operação do lead (`none` quando ausente). */
  function operationLabel(op: ContactOperationType | null): string {
    if (!op) return t('contact.messages.none');
    return t(`contact.messages.operations.${op}`);
  }

  return (
    <ContentLayout
      header={<Header variant="h1" description={t('contact.messages.subtitle')}>{t('contact.messages.title')}</Header>}
    >
      {state.kind === 'LOADING' && (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
        </Box>
      )}

      {state.kind === 'READY' && (
        <Table<ContactSubmissionRecord>
          items={items}
          trackBy="id"
          variant="container"
          selectionType="multi"
          selectedItems={selectedVisible}
          onSelectionChange={(e): void => {
            setSelected(e.detail.selectedItems);
          }}
          ariaLabels={{
            selectionGroupLabel: t('contact.messages.title'),
            itemSelectionLabel: (_data, row): string => row.name,
          }}
          header={
            <Header
              variant="h2"
              counter={selectedVisible.length > 0 ? `(${String(selectedVisible.length)})` : undefined}
              actions={
                <SpaceBetween size="xs" direction="horizontal">
                  <Button
                    loading={applying === 'READ'}
                    disabled={selectedVisible.length === 0 || (applying != null && applying !== 'READ')}
                    onClick={(): void => {
                      void changeStatus('READ');
                    }}
                  >
                    {t('contact.messages.actions.markRead')}
                  </Button>
                  <Button
                    loading={applying === 'ARCHIVED'}
                    disabled={selectedVisible.length === 0 || (applying != null && applying !== 'ARCHIVED')}
                    onClick={(): void => {
                      void changeStatus('ARCHIVED');
                    }}
                  >
                    {t('contact.messages.actions.archive')}
                  </Button>
                  <SegmentedControl
                    selectedId={filter}
                    onChange={(e): void => {
                      setFilter(e.detail.selectedId as StatusFilter);
                    }}
                    options={[
                      { id: 'ALL', text: t('contact.messages.filter.all') },
                      ...ALL_CONTACT_SUBMISSION_STATUSES.map((s) => ({
                        id: s,
                        text: t(`contact.messages.filter.${s}`),
                      })),
                    ]}
                  />
                </SpaceBetween>
              }
            >
              {t('contact.messages.title')}
            </Header>
          }
          empty={
            <Box textAlign="center" color="inherit">
              {t('contact.messages.empty')}
            </Box>
          }
          columnDefinitions={[
            {
              id: 'createdAt',
              header: t('contact.messages.columns.date'),
              cell: (row): React.ReactNode =>
                row.createdAtMillis != null ? formatDateTime(row.createdAtMillis, i18n.language) : '—',
            },
            {
              id: 'contact',
              header: t('contact.messages.columns.name'),
              cell: (row): React.ReactNode => (
                <SpaceBetween size="xxs">
                  <Box variant="strong">{row.name}</Box>
                  <Link href={`mailto:${row.email}`} fontSize="body-s">
                    {row.email}
                  </Link>
                  {row.phone ? (
                    <Link href={`tel:${row.phone}`} fontSize="body-s">
                      {row.phone}
                    </Link>
                  ) : null}
                </SpaceBetween>
              ),
            },
            {
              id: 'origin',
              header: t('contact.messages.columns.origin'),
              cell: (row): React.ReactNode =>
                row.channel === 'WHATSAPP' ? (
                  <Badge color="green">{t('contact.messages.channel.WHATSAPP')}</Badge>
                ) : (
                  <Badge color="blue">{t('contact.messages.channel.FORM')}</Badge>
                ),
            },
            {
              id: 'operation',
              header: t('contact.messages.columns.operation'),
              cell: (row): React.ReactNode => operationLabel(row.operationType),
            },
            {
              id: 'locale',
              header: t('contact.messages.columns.locale'),
              cell: (row): React.ReactNode => row.locale.toUpperCase(),
            },
            {
              id: 'message',
              header: t('contact.messages.columns.message'),
              cell: (row): React.ReactNode => (
                <Box variant="span">
                  <span style={{ whiteSpace: 'pre-wrap' }}>{row.message}</span>
                </Box>
              ),
            },
            {
              id: 'status',
              header: t('contact.messages.columns.status'),
              cell: (row): React.ReactNode => (
                <Badge color={STATUS_COLOR[row.status]}>{t(`contact.messages.status.${row.status}`)}</Badge>
              ),
            },
          ]}
        />
      )}
    </ContentLayout>
  );
}
